/**
 * Клиент дашборда (E12a). Типы согласованы с сервером ПО ЗНАЧЕНИЯМ — формы из
 * `apps/api/src/summary/dashboard.ts`.
 *
 * Ни одной цифры здесь не считается: агрегаты приходят готовыми теми же функциями, что считают
 * сводку и карточку договора. Экран только рисует — иначе дашборд и сводка разошлись бы (E12a §4).
 */

import type { ApiResult } from "./contracts";
import type { ObligationStatus } from "./obligations";
import type { SummaryRow } from "./summary";

/** Точка динамики: сколько обязательств было просрочено на конец недели. */
export interface TrendPoint {
  readonly date: string;
  /** Просрочки контрагентов (должник — они). */
  readonly they: number;
  /** Наши собственные просрочки (должник — мы). */
  readonly we: number;
}

/** Сегмент возрастной структуры: 0–30 / 31–60 / 61–90 / более 90 дней просрочки. */
export interface AgingBucket {
  readonly id: string;
  readonly label: string;
  readonly count: number;
  readonly debt: string;
}

export interface TopCounterpartyRow {
  readonly counterpartyId: string;
  readonly counterpartyName: string;
  /** Обязательств с НАЧИСЛЕННЫМИ санкциями — сюда входит и погашенное с опозданием. */
  readonly count: number;
  readonly sanctions: string;
}

export interface StatusSlice {
  readonly status: ObligationStatus;
  readonly label: string;
  readonly count: number;
}

export interface Dashboard {
  readonly today: string;
  /** Есть ли обязательства вообще — различает «данных нет» и «просрочек нет» (E12a §11 п. 6). */
  readonly hasData: boolean;
  readonly trend: { readonly weeks: TrendPoint[] };
  readonly aging: {
    readonly buckets: AgingBucket[];
    readonly count: number;
    readonly debt: string;
  };
  readonly top: {
    readonly rows: TopCounterpartyRow[];
    readonly others: { readonly count: number; readonly sanctions: string };
    readonly total: string;
  };
  readonly statuses: StatusSlice[];
  readonly escalations: {
    readonly total: number;
    readonly they: number;
    readonly we: number;
    readonly rows: SummaryRow[];
  };
}

/**
 * Дашборд (GET /api/summary/dashboard). Право `summary:view` проверяет сервер — то же, что у
 * сводки: делопроизводителю ответит 403, и скрытая вкладка это не заменяет (правило 5).
 */
export async function fetchDashboard(): Promise<ApiResult<Dashboard>> {
  let res: Response;
  try {
    res = await fetch("/api/summary/dashboard", {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }

  if (res.ok) {
    const body = (await res.json().catch(() => null)) as Dashboard | null;
    if (body === null) {
      return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
    }
    return { ok: true, value: body };
  }

  if (res.status === 401) {
    return { ok: false, reason: "unauthorized", message: "Сессия истекла. Войдите заново." };
  }
  const body = (await res.json().catch(() => null)) as { message?: string } | null;
  if (res.status === 403) {
    return { ok: false, reason: "forbidden", message: body?.message ?? "Недостаточно прав." };
  }
  return {
    ok: false,
    reason: "network",
    message: body?.message ?? "Не удалось загрузить дашборд.",
  };
}

/**
 * Адреса переходов из блоков дашборда в реестр обязательств — ОДНО место на весь экран.
 *
 * Фильтры существующие (план §7, вариант «а»): по статусу, стороне-должнику и контрагенту.
 * Фильтра «диапазон дней просрочки» у реестра нет, поэтому сегмент возрастной структуры ведёт в
 * общий отбор просрочек контрагентов, и экран говорит об этом прямо. Точный фильтр заведён
 * строкой бэклога, а не сделан по ходу (правило 27).
 */
export const registryHref = {
  status: (status: ObligationStatus): string => `/obligations?status=${status}`,
  /** Просрочки контрагентов перед нами — сегменты возрастной структуры и линия динамики. */
  overdueOfCounterparties: (): string => "/obligations?status=overdue&debtor=they",
  /** Наши собственные просрочки — вторая линия динамики. */
  overdueOfOurs: (): string => "/obligations?status=overdue&debtor=we",
  counterparty: (id: string): string => `/obligations?counterpartyId=${encodeURIComponent(id)}`,
  allOverdue: (): string => "/obligations?status=overdue",
};
